/**
 * Polling hook with two rules built in: **never let polls pile up**, and **never show one subject's
 * data under another's name**.
 *
 * Some of these endpoints are expensive on the far side — a session's event feed is a full logcat
 * dump over adb on the build machine, and overlapping dumps starve input and video on the very hub
 * we are watching. So a tick never runs while the previous one is in flight; it waits for it.
 *
 * Waits, rather than is dropped: dropping cost a real bug, and both rules are explained in
 * `pollRules.ts`, where they live so they can be tested without a DOM.
 */

import { useEffect, useRef, useState } from 'react'

import { forSubject, ticker } from './pollRules'

export interface PollState<T> {
  data: T | null
  error: Error | null
  loading: boolean
  refresh: () => void
}

export function usePoll<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
  deps: unknown[],
  enabled = true,
): PollState<T> {
  // Data and error are stored **with the deps they were fetched for**, so a render for a different
  // subject cannot show them. Kept as one snapshot rather than cleared in an effect: clearing after the
  // fact still paints one frame of the wrong app's builds.
  const [snap, setSnap] = useState<{ key: readonly unknown[]; value: T | null; error: Error | null }>({
    key: deps,
    value: null,
    error: null,
  })
  const [loading, setLoading] = useState(false)
  const [nonce, setNonce] = useState(0)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const depsRef = useRef<readonly unknown[]>(deps)
  depsRef.current = deps

  useEffect(() => {
    if (!enabled) {
      // Clear the last error when polling stops. Without this it stays on screen forever: the events
      // feed 404s on the poll that races a session ending, `enabled` then goes false, and the panel
      // shows a permanent red "HTTP 404" next to "open a session to see events" — an alarm about
      // something that is over, which is worse than no alarm because it trains people to ignore red.
      setSnap((s) => ({ ...s, error: null }))
      return
    }
    let alive = true

    const { tick } = ticker(async () => {
      // Read at call time, not at effect time: a queued tick runs after the deps changed, and it must
      // fetch — and be filed under — the subject asked for last.
      const key = depsRef.current
      setLoading(true)
      try {
        const value = await fetcherRef.current()
        if (alive) setSnap({ key, value, error: null })
      } catch (e) {
        if (alive) {
          setSnap({ key, value: null, error: e instanceof Error ? e : new Error(String(e)) })
        }
      } finally {
        if (alive) setLoading(false)
      }
    })

    void tick()
    if (intervalMs <= 0) return () => {
      alive = false
    }
    const id = setInterval(() => void tick(), intervalMs)
    return () => {
      alive = false
      clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, intervalMs, enabled, nonce])

  return {
    data: forSubject({ key: snap.key, value: snap.value }, deps),
    error: forSubject({ key: snap.key, value: snap.error }, deps),
    loading,
    refresh: () => setNonce((n) => n + 1),
  }
}
